import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Logo,
  HomeIcon,
  BookIcon,
  CalendarIcon,
  FileIcon,
  SparkleIcon,
  LogoutIcon,
  MenuIcon,
  CloseIcon,
  IconProps,
} from '../UI/Icons';

interface NavItem {
  path: string;
  label: string;
  icon: React.FC<IconProps>;
}

const navItems: NavItem[] = [
  { path: '/dashboard', label: 'Dashboard', icon: HomeIcon },
  { path: '/courses', label: 'Courses', icon: BookIcon },
  { path: '/schedule', label: 'Schedule', icon: CalendarIcon },
  { path: '/transcript', label: 'Transcript', icon: FileIcon },
  { path: '/advisor', label: 'AI Advisor', icon: SparkleIcon },
];

const initialsOf = (name?: string, email?: string) => {
  const source = (name || email || 'U').trim();
  const parts = source.split(/[\s@._-]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? 'U') + (parts[1]?.[0] ?? '')).toUpperCase();
};

const Layout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const activeItem = navItems.find((item) => location.pathname.startsWith(item.path));

  return (
    <div className="min-h-screen bg-[#E9E9EE] p-2 sm:p-4 lg:p-6">
      <div className="mx-auto flex h-[calc(100vh-1rem)] max-w-[1600px] flex-col overflow-hidden rounded-2xl bg-white shadow-shell sm:h-[calc(100vh-2rem)] sm:rounded-3xl lg:h-[calc(100vh-3rem)]">
        {/* ---------- Top bar ---------- */}
        <header className="flex shrink-0 items-center gap-3 border-b border-canvas-200 px-3 py-3 sm:gap-4 sm:px-5">
          <button
            onClick={() => setMobileNavOpen(!mobileNavOpen)}
            className="rounded-xl p-2 text-canvas-700 transition-colors hover:bg-canvas-100 lg:hidden"
            aria-label="Toggle navigation"
          >
            {mobileNavOpen ? <CloseIcon /> : <MenuIcon />}
          </button>

          <Link to="/dashboard" className="flex items-center gap-2.5">
            <Logo size={34} />
            <span className="hidden font-display text-lg font-semibold tracking-tight text-canvas-900 sm:block">
              UW Pathfinder
            </span>
          </Link>

          <div className="ml-auto flex items-center gap-2">
            <span className="hidden text-right text-sm leading-tight lg:block">
              <span className="block font-medium text-canvas-900">
                {user?.full_name || 'Student'}
              </span>
              <span className="block text-xs text-canvas-500">{user?.email}</span>
            </span>
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-ink text-xs font-semibold text-white">
              {initialsOf(user?.full_name, user?.email)}
            </span>
            <button
              onClick={handleLogout}
              title="Log out"
              className="rounded-xl p-2 text-canvas-600 transition-colors hover:bg-canvas-100 hover:text-canvas-900"
            >
              <LogoutIcon />
            </button>
          </div>
        </header>

        <div className="flex min-h-0 flex-1">
          {/* ---------- Icon rail (desktop) ---------- */}
          <nav className="hidden w-[68px] shrink-0 flex-col items-center gap-1 border-r border-canvas-200 py-4 lg:flex">
            {navItems.map(({ path, label, icon: Icon }) => {
              const active = location.pathname.startsWith(path);
              return (
                <Link
                  key={path}
                  to={path}
                  title={label}
                  className={`group relative flex h-11 w-11 items-center justify-center rounded-xl transition-colors ${
                    active
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-canvas-500 hover:bg-canvas-100 hover:text-canvas-800'
                  }`}
                >
                  {active && (
                    <span className="absolute -left-[14px] h-6 w-[3px] rounded-r-full bg-ink" />
                  )}
                  <Icon width={21} height={21} />
                  {/* Hover tooltip */}
                  <span className="pointer-events-none absolute left-full z-30 ml-3 whitespace-nowrap rounded-lg bg-ink px-2.5 py-1.5 text-xs font-medium text-white opacity-0 shadow-card transition-opacity group-hover:opacity-100">
                    {label}
                  </span>
                </Link>
              );
            })}
          </nav>

          {/* ---------- Mobile nav drawer ---------- */}
          {mobileNavOpen && (
            <>
              <button
                className="fixed inset-0 z-30 bg-canvas-900/20 lg:hidden"
                onClick={() => setMobileNavOpen(false)}
                aria-label="Close navigation"
              />
              <nav className="absolute left-0 top-[61px] z-40 w-60 animate-fade-in rounded-br-2xl border-r border-canvas-200 bg-white p-3 shadow-card-hover lg:hidden">
                {navItems.map(({ path, label, icon: Icon }) => {
                  const active = location.pathname.startsWith(path);
                  return (
                    <Link
                      key={path}
                      to={path}
                      onClick={() => setMobileNavOpen(false)}
                      className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                        active
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-canvas-600 hover:bg-canvas-100 hover:text-canvas-900'
                      }`}
                    >
                      <Icon width={19} height={19} />
                      {label}
                    </Link>
                  );
                })}
              </nav>
            </>
          )}

          {/* ---------- Page content ---------- */}
          <main className="custom-scrollbar min-w-0 flex-1 overflow-y-auto bg-canvas-50/60">
            {/* Mobile page title so context isn't lost without the rail */}
            <div className="border-b border-canvas-200 bg-white px-4 py-3 lg:hidden">
              <span className="font-display text-base font-semibold text-canvas-900">
                {activeItem?.label ?? 'UW Pathfinder'}
              </span>
            </div>
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
};

export default Layout;
